import { Col, Layout, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { usePostHog } from "@posthog/react";
import DirectLinksComponent from "../../../components/DirectLinksComponent";
import PresentationVideoSwiper from "../../../components/discover/common/Swiper/PresentationVideoSwiperComponent";
import SwiperGifComponent from "../../../components/discover/common/Swiper/SwiperGifComponent";
import HeaderInfoDiscoverComponent from "../../../components/discover/creator/HeaderInfoDiscoverComponent";
import FooterComponent from "../../../components/FooterComponent";
import IsSearchingComponent from "../../../components/Search/IsSearchingComponent";
import SearchComponent from "../../../components/Search/SearchComponent";
import { CREATOR, FOOTER_IDS } from "../../../helpers/constant";
import useFetchUserInfos from "../../../hooks/userHooks/useFetchUserInfos";
import useFetchUserSettings from "../../../hooks/userHooks/useFetchUserSettings";
import { clearScrollPosition, resetFanDiscovers } from "../../../redux/discovers/discoversSlice";
import { AppDispatch } from "../../../redux/store";
import { resetList, updateTutorialVisibilityStatus } from "../../../redux/users/usersSlice";
import { isMobile } from "../../../utls/FunctionsUtil";
import RecentVisitedProfile from "../RecentVisitedProfile";
import "./index.less";

const DiscoverPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const posthog = usePostHog();
  const [searchValue, setSearchValue] = useState("");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [showCancelButton, setShowCancelButton] = useState<boolean>(false);
  const recentRef = useRef<HTMLInputElement | null>(null);
  const [showRecentVisitedProfile, setShowRecentVisitedProfile] = useState(false);
  const { currentUser: user } = useFetchUserInfos();
  const { currentUserSettings } = useFetchUserSettings();
  const creatorHeaderInfoVisible = (currentUserSettings?.expiredDiscovers || !currentUserSettings?.hasDiscovers) && user?.infos.type === CREATOR;
  // Clear scroll position if user navigates directly to discover page (not from internal navigation)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clear scroll position when page is refreshed or closed
      dispatch(clearScrollPosition());
    };

    // Check if user came from external source (refresh, direct URL, etc.)
    const navigationEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    if (navigationEntries.length > 0) {
      const navigationType = navigationEntries[0].type;
      // Clear scroll position for reload, navigate (direct URL), or back_forward from external
      if (navigationType === "reload" || navigationType === "navigate") {
        dispatch(clearScrollPosition());
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!currentUserSettings?.visibilityStatus?.hasSeenPresentationVideoTutorial) {
      const handleInteraction = () => handleCloseOverlay();

      const eventType = isMobile() ? "touchend" : "click";
      let timeoutId: number;

      timeoutId = window.setTimeout(() => {
        window.addEventListener(eventType, handleInteraction);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener(eventType, handleInteraction);
      };
    }
  }, [currentUserSettings]);

  const handleCloseOverlay = async () => {
    dispatch(updateTutorialVisibilityStatus("hasSeenPresentationVideoTutorial"));
  };

  const handleInputClick = () => {
    posthog.capture('discover_search_focused');
    setShowRecentVisitedProfile(true);
    setShowCancelButton(true);
  };

  const searchCreators = (value: string) => {
    if (value !== undefined && value !== "") {
      posthog.capture('discover_search_initiated', {
        search_query: value
      });
      setSearchValue(value);
      setIsSearching(true);
    }
    if (value === "") {
      clearSearch();
    }
  };

  const clearSearch = () => {
    setSearchValue("");
    dispatch(resetList());
    setIsSearching(false);
    setShowRecentVisitedProfile(true);
  };

  const cancelSearching = () => {
    posthog.capture('discover_search_cancelled');
    setSearchValue("");
    setIsSearching(false);
    setShowRecentVisitedProfile(false);
    setShowCancelButton(false);
    dispatch(resetList());
  };

  return (
    <Layout className="discover-content-wrapper gradient-background-black-blue no-scroll-horizontal">
      <Layout.Header className="discover-header-wrapper">
        {showRecentVisitedProfile && (
          <div className="discover-header-is-searching-wrapper">
            <Col span={showCancelButton ? 20 : 24}>
              <SearchComponent
                onClick={handleInputClick}
                placeholder={`${t("search-2")}`}
                handleChange={(e) => searchCreators(e.target.value)}
                value={searchValue}
              />
            </Col>
            {showCancelButton && (
              <Col span={4} className="cancel-text-col">
                <Typography.Text className="cancel-search-button-text" onClick={cancelSearching}>
                  {t("cancel")}
                </Typography.Text>
              </Col>
            )}
          </div>
        )}
      </Layout.Header>
      <Layout.Content className="background-transparent">
        {creatorHeaderInfoVisible && !isSearching && !showRecentVisitedProfile && (
          <HeaderInfoDiscoverComponent renew={currentUserSettings?.expiredDiscovers} />
        )}
        {isSearching ? (
          <div className="padding-inline-container">
            <IsSearchingComponent isSearching={isSearching} searchValue={searchValue} />
          </div>
        ) : (
          !isSearching &&
          showRecentVisitedProfile && (
            <div>
              <div className="direct-links-wrapper">
                <DirectLinksComponent />
              </div>
              <div className="padding-inline-container" ref={recentRef}>
                <RecentVisitedProfile />
              </div>
            </div>
          )
        )}

        <PresentationVideoSwiper isVisible={!isSearching && !showRecentVisitedProfile} searchClick={handleInputClick} shouldShow={creatorHeaderInfoVisible && !isSearching && !showRecentVisitedProfile} />
        {currentUserSettings && !currentUserSettings.visibilityStatus.hasSeenPresentationVideoTutorial && (
          <div className="swiper-gif-overlay">
            <SwiperGifComponent />
          </div>
        )}
      </Layout.Content>
      <FooterComponent page={FOOTER_IDS.SEARCH} black={false} />
    </Layout>
  );
};

export default DiscoverPage;
